import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/**
 * Portal do desenvolvedor e sandbox
 * (SPEC-20260929-2025-plugin-portal-do-desenvolvedor / -plugin-sandbox).
 *
 * Uma consulta só para as três coisas que o portal mostra — cadastro, plugins
 * publicados e sandboxes. São a mesma página e mudam juntas; três consultas
 * separadas piscariam em ordens diferentes.
 */

export type StatusDeDesenvolvedor = 'pendente' | 'aprovado' | 'recusado' | 'suspenso'

export type Desenvolvedor = {
  id: string
  memberId: string
  displayName: string
  contactEmail: string
  website: string | null
  about: string | null
  status: StatusDeDesenvolvedor
  statusReason: string | null
  criadoEm: string
  revisadoEm: string | null
}

export type MeuPlugin = {
  id: string
  key: string
  name: Record<string, string>
  version: string
  baseUrl: string
  manifestUrl: string
  scopes: string[]
  arquivado: boolean
  /** Agregado: quais workspaces instalaram não é da conta do autor. */
  instalacoes: number
}

export type Sandbox = {
  id: string
  name: string
  slug: string
  seededAt: string | null
  itens: number
}

export type Portal = {
  cadastro: Desenvolvedor | null
  plugins: MeuPlugin[]
  sandboxes: Sandbox[]
  maximoDeSandboxes: number
}

const CHAVE = ['developer', 'portal']
const CHAVE_CONSOLE = ['console', 'developers']

const comoJson = <T,>(p: Promise<unknown>): Promise<T> => p as Promise<T>

export function usePortalDoDesenvolvedor() {
  return useQuery({
    queryKey: CHAVE,
    queryFn: async () => comoJson<Portal>((await api.developer.$get()).json()),
  })
}

export function useCadastrarDesenvolvedor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: {
      displayName: string
      contactEmail: string
      website?: string | null
      about?: string | null
    }) => (await api.developer.$post({ json: entrada })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useCriarSandbox() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (nome?: string) =>
      (await api.developer.sandboxes.$post({ json: nome ? { nome } : {} })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE }),
  })
}

export function useRecriarSandbox() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.developer.sandboxes[':id'].reset.$post({ param: { id } })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE }),
  })
}

/* --------------------------------------------------------- super admin --- */

export type DesenvolvedorNoConsole = Desenvolvedor & {
  memberName: string
  memberEmail: string
  plugins: number
}

export function useDesenvolvedoresDoConsole(status?: StatusDeDesenvolvedor) {
  return useQuery({
    queryKey: [...CHAVE_CONSOLE, status ?? 'todos'],
    queryFn: async () =>
      comoJson<{ desenvolvedores: DesenvolvedorNoConsole[] }>(
        (await api.console.developers.$get({ query: status ? { status } : {} })).json(),
      ),
  })
}

export function useRevisarDesenvolvedor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: { id: string; status: StatusDeDesenvolvedor; motivo?: string }) =>
      (
        await api.console.developers[':id'].review.$post({
          param: { id: entrada.id },
          json: { status: entrada.status, motivo: entrada.motivo ?? null },
        })
      ).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE_CONSOLE }),
  })
}

/* --------------------------------------------------------- marketplace --- */

export type StatusDePlugin = 'submetido' | 'aprovado' | 'recusado'

export type PluginParaRevisao = {
  id: string
  key: string
  name: Record<string, string>
  version: string
  baseUrl: string
  manifestUrl: string
  scopes: string[]
  status: StatusDePlugin
  reviewReason: string | null
  desenvolvedor: { id: string; displayName: string; contactEmail: string } | null
  extensoes: number
}

/** O desenvolvedor submete o próprio plugin. */
export function useSubmeterPlugin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (manifestUrl: string) =>
      (await api.developer.plugins.$post({ json: { manifestUrl } })).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CHAVE }),
  })
}

const CHAVE_REVISAO = ['console', 'plugins', 'review']

export function usePluginsParaRevisao(status?: StatusDePlugin) {
  return useQuery({
    queryKey: [...CHAVE_REVISAO, status ?? 'todos'],
    queryFn: async () =>
      comoJson<{ plugins: PluginParaRevisao[] }>(
        (await api.console.plugins.review.$get({ query: status ? { status } : {} })).json(),
      ),
  })
}

export function useRevisarPlugin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: { id: string; status: StatusDePlugin; motivo?: string }) =>
      (
        await api.console.plugins[':id'].review.$post({
          param: { id: entrada.id },
          json: { status: entrada.status, motivo: entrada.motivo ?? null },
        })
      ).json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_REVISAO })
      void queryClient.invalidateQueries({ queryKey: ['console', 'plugins'] })
      // aprovar faz o plugin aparecer no catálogo dos workspaces
      void queryClient.invalidateQueries({ queryKey: ['plugins'] })
    },
  })
}

/** Reaceitar escopos depois de uma versão nova pedir mais. */
export function useReaceitarEscopos() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entrada: { installationId: string; scopes: string[] }) =>
      (
        await api.plugins.installations[':id']['accept-scopes'].$post({
          param: { id: entrada.installationId },
          json: { scopes: entrada.scopes },
        })
      ).json(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plugins'] }),
  })
}
